/**
 * EPIC-DM01: Demo Mode Card
 *
 * Composable card container with variant styles for demo screens.
 * Accepts optional `header` / `footer` render props plus children for body.
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/* ------------------------------------------------------------------ */
/*  Variants                                                          */
/* ------------------------------------------------------------------ */

const demoCardVariants = cva(
  'rounded-2xl p-6 shadow-demo-sm transition-shadow duration-200',
  {
    variants: {
      variant: {
        default: 'bg-demo-surface border border-gray-200',
        highlight: 'bg-demo-green-light border border-demo-green/20',
        selected:
          'bg-demo-surface ring-2 ring-demo-green border border-transparent',
        warning: 'bg-demo-gold-light border border-demo-gold/30',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

export interface DemoCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof demoCardVariants> {
  /** Rendered above the body, separated by a bottom border */
  header?: React.ReactNode;
  /** Rendered below the body, separated by a top border */
  footer?: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

const DemoCard = React.forwardRef<HTMLDivElement, DemoCardProps>(
  ({ className, variant, header, footer, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(demoCardVariants({ variant }), className)}
        {...props}
      >
        {header && (
          <div className="mb-4 pb-4 border-b border-gray-200">{header}</div>
        )}

        <div className="text-demo-base text-demo-text">{children}</div>

        {footer && (
          <div className="mt-4 pt-4 border-t border-gray-200">{footer}</div>
        )}
      </div>
    );
  },
);

DemoCard.displayName = 'DemoCard';

export { DemoCard, demoCardVariants };
